import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { match } from 'ts-pattern';

import { useTranslator } from '@/hooks/useTranslator';
import { BitsoBank } from '@/types/bitsoBank';
import {
  ACHAccountDetails,
  COPAccountDetails,
  CVUAccountDetails,
  FiatAccount,
  PIXAccountDetails,
  SpeiAccountDetails,
  SwiftAccountDetails,
  WireAccountDetails,
} from '@/types/contacts';
import {
  isACHAccountDetails,
  isCOPAccountDetails,
  isCVUAccountDetails,
  isPIXAccountDetails,
  isSpeiAccountDetails,
  isSwiftAccountDetails,
  isWireAccountDetails,
} from '@/types/contacts.ext';
import { CurrencyCodeType } from '@/types/currency';

interface Props {
  readonly account: FiatAccount;
  readonly banks: readonly BitsoBank[];
}

export const FiatAccountTransactionDetailsItem: React.FC<Props> = (
  props: Props,
): React.ReactElement => {
  const { account, banks } = props;

  const tr = useTranslator();

  return match(account.accountDetails)
    .when(
      isACHAccountDetails,
      (details: ACHAccountDetails): React.ReactElement => (
        <Box>
          <Typography variant="body2" color="text.primary">
            {tr(account.name)}
          </Typography>
          <Typography
            variant="body2"
            color="custom.LightGray"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis">
            {[CurrencyCodeType.USD, tr(details.achDetails.bankInfo.name)]
              .filter((value: string): boolean => value !== '')
              .join(separator)}
          </Typography>
        </Box>
      ),
    )
    .when(
      isWireAccountDetails,
      (details: WireAccountDetails): React.ReactElement => (
        <Box>
          <Typography variant="body2" color="text.primary">
            {tr(account.name)}
          </Typography>
          <Typography
            variant="body2"
            color="custom.LightGray"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis">
            {[CurrencyCodeType.USD, tr(details.wireDetails.bankInfo.name)]
              .filter((value: string): boolean => value !== '')
              .join(separator)}
          </Typography>
        </Box>
      ),
    )
    .when(
      isSwiftAccountDetails,
      (details: SwiftAccountDetails): React.ReactElement => (
        <Box>
          <Typography variant="body2" color="text.primary">
            {tr(account.name)}
          </Typography>
          <Typography
            variant="body2"
            color="custom.LightGray"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis">
            {[CurrencyCodeType.USD, tr(details.swiftDetails.bankInfo.name)]
              .filter((value: string): boolean => value !== '')
              .join(separator)}
          </Typography>
        </Box>
      ),
    )
    .when(
      isPIXAccountDetails,
      (details: PIXAccountDetails): React.ReactElement => (
        <Box>
          <Typography variant="body2" color="text.primary">
            {tr(account.name)}
          </Typography>
          <Typography
            variant="body2"
            color="custom.LightGray"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis">
            {[CurrencyCodeType.BRL, details.pixDetails.pixKey]
              .filter((value: string): boolean => value !== '')
              .join(separator)}
          </Typography>
        </Box>
      ),
    )
    .when(
      isSpeiAccountDetails,
      (details: SpeiAccountDetails): React.ReactElement => (
        <Box>
          <Typography variant="body2" color="text.primary">
            {tr(account.name)}
          </Typography>
          <Typography
            variant="body2"
            color="custom.LightGray"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis">
            {[CurrencyCodeType.MXN, details.speiDetails.clabe]
              .filter((value: string): boolean => value !== '')
              .join(separator)}
          </Typography>
        </Box>
      ),
    )
    .when(isCOPAccountDetails, (details: COPAccountDetails): React.ReactElement => {
      const bank = banks.find(
        (bank: BitsoBank): boolean => bank.code === details.copDetails.bankCode,
      );

      return (
        <Box>
          <Typography variant="body2" color="text.primary">
            {tr(account.name)}
          </Typography>
          <Typography
            variant="body2"
            color="custom.LightGray"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis">
            {[CurrencyCodeType.USD, tr(bank?.name ?? '')]
              .filter((value: string): boolean => value !== '')
              .join(separator)}
          </Typography>
        </Box>
      );
    })
    .when(
      isCVUAccountDetails,
      (details: CVUAccountDetails): React.ReactElement => (
        <Box>
          <Typography variant="body2" color="text.primary">
            {tr(account.name)}
          </Typography>
          <Typography
            variant="body2"
            color="custom.LightGray"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis">
            {[CurrencyCodeType.ARS, details.cvuDetails.cvu]
              .filter((value: string): boolean => value !== '')
              .join(separator)}
          </Typography>
        </Box>
      ),
    )
    .otherwise((): React.ReactElement => <></>);
};
const separator = ' | ';
